import { useCallback, useState } from 'react';
import type { CSSProperties, RefObject } from 'react';

type Size = { width: number; height: number };

type HeldSize = {
  /** Record the element's current size; it will not shrink below the largest one recorded. */
  hold: () => void;
  held: boolean;
  style: CSSProperties | undefined;
};

/**
 * Keeps an element (a dialog switching between views) at the largest size it has
 * been held at, capped to the window, so a switch never shrinks it.
 */
export const useHeldSize = (ref: RefObject<HTMLElement | null>): HeldSize => {
  const [size, setSize] = useState<Size | null>(null);
  const hold = useCallback(() => {
    const rect = ref.current?.getBoundingClientRect();

    if (!rect) return;
    setSize((held) => ({
      width: Math.max(held?.width ?? 0, rect.width),
      height: Math.max(held?.height ?? 0, rect.height),
    }));
  }, [ref]);
  const style = size
    ? { minWidth: `min(${size.width}px, 100%)`, minHeight: `min(${size.height}px, calc(100dvh - 32px))` }
    : undefined;

  return { hold, held: size !== null, style };
};
